import React, { useEffect, useMemo, useState } from "react";
import Head from "next/head";
import type { AppProps } from "next/app";
import { usePathname } from "next/navigation";
import { SessionProvider } from "next-auth/react";

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

import NProgress from "nprogress";

import { Auth, Amplify, Hub } from "aws-amplify";
import awsExports from "@/aws-exports";
import { Authenticator } from "@aws-amplify/ui-react";

import { ConfigProvider } from "antd";
import { StyleProvider } from "@ant-design/cssinjs";
import locale from "antd/locale/en_US";

import useLayoutStore from "@/store/useLayoutStore";
import useAuthStore from "@/store/useAuthStore";
import { useInitiateEnv, useInitiateModule } from "@/store/useEnvironmentStore";

import {
  theme,
  STG_HOST,
  HBAS_HOST,
  MILKEN_HOST,
  TESTING_HOST,
} from "@/utils/constants";
import {
  saveQueryData,
  loadQueryData,
  removeQueryData,
} from "@/utils/reactQueryPersistence";
import { useKeySearch } from "@/utils/helper";

import OnBoarding from "@/components/Onboarding";
import ModalResult from "@/components/modal-result";
import PointsProvider from "@/components/PointsProvider";
import ProtectedRoutes from "@/components/ProtectedRoutes";
import ErrorBoundary from "@/components/ErrorBoundary";
import DevDebugTools from "@/components/DevDebugTools";
import UserGuideVideo from "@/components/userGuideVideo";
import { ModalReadMore } from "@/components/ModalReadMore";

import { push } from "@socialgouv/matomo-next";
import { setSentryUser, clearSentryUser } from "@/sentry";

import "dayjs/locale/en";
import "antd/dist/reset.css";
import "@/styles/tailwind.scss";
import "@/styles/global.scss";
import "nprogress/nprogress.css";
import "swiper/css";
import "swiper/css/grid";
import { useMatomoConfig } from "@/hooks/useMatomo";
import AuthListener from "@/components/AuthListener";

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      staleTime: 0, // 5 minutes
      cacheTime: 100 * 60 * 60 * 1000,
    },
  },
});

Amplify.configure({
  ...awsExports,
  ssr: true,

  API: {
    graphql_headers: async () => {
      try {
        const session = await Auth.currentSession();
        return {
          Authorization: session.getIdToken().getJwtToken(),
        };
      } catch (error) {
        return {};
      }
    },
  },
});

export default function App({
  Component,
  pageProps: { session, ...pageProps },
  router,
}: AppProps) {
  const [isMounted, isMountedSet] = useState(false);

  useInitiateEnv();
  useInitiateModule();
  useMatomoConfig();

  const pathname = usePathname();
  const [userChanged, setUserChanged] = useState(false);
  const setRightCta = useLayoutStore.useSetRightCta();

  // Get user data from auth store
  const user = useAuthStore.useUser();
  const isAuthenticated = useAuthStore.useIsAuthenticated();

  useEffect(() => {
    (async () => {
      try {
        const user = await Auth.currentAuthenticatedUser();
        const userId = user.attributes.sub;

        queryClient.clear();

        await removeQueryData(userId);

        loadQueryData(queryClient, userId);
        // Save the cache whenever the window is closed or refreshed
        const handleWindowClose = () => {
          saveQueryData(queryClient, userId);
        };

        window.addEventListener("beforeunload", handleWindowClose);
        return () => {
          window.removeEventListener("beforeunload", handleWindowClose);
        };
      } catch (err) {
        console.log(err);
      } finally {
        setUserChanged(false); // Reset userChanged after loading data
      }
    })();
  }, [userChanged]);

  useEffect(() => {
    isMountedSet(true);

    if (isAuthenticated) {
      if (user?.profile && user?.bio) {
        const userId = user.profile.user_id || user.profile.sub;
        const userName =
          user.profile.firstName && user.profile.lastName
            ? `${user.profile.firstName} ${user.profile.lastName}`
            : user.profile.username || "Unknown User";
        const userEmail = user.profile.email || user.profile.email;

        // set active user
        setSentryUser({
          id: userId,
          email: userEmail,
          username: userName,
        });
      }
    }
  }, [isAuthenticated, user]);

  useEffect(() => {
    const handleRouteStart = () => {
      setRightCta(null);
      NProgress.start();
    };
    const handleRouteDone = (url: string) => {
      NProgress.done();

      // Set custom variables on every page change if user is authenticated
      if (isAuthenticated && user?.profile && user?.bio) {
        const userName =
          user.bio.firstName && user.bio.lastName
            ? `${user.bio.firstName} ${user.bio.lastName}`
            : user.bio.username || "Unknown User";
        const userEmail = user.bio.email || user.profile.email;

        // Set custom variables for this page
        push(["setCustomVariable", 1, "UserName", userName, "page"]);
        push(["setCustomVariable", 2, "UserEmail", userEmail, "page"]);
        push([
          "setCustomVariable",
          3,
          "UserType",
          user.bio.type || "Unknown",
          "page",
        ]);
        push([
          "setCustomVariable",
          4,
          "OrganizationGroup",
          user.bio.organizationGroup || "Unknown",
          "page",
        ]);
      }

      // Track the page view (this will register the custom variables)
      push(["trackPageView"]);
    };

    router.events.on("routeChangeStart", handleRouteStart);
    router.events.on("routeChangeComplete", handleRouteDone);
    router.events.on("routeChangeError", handleRouteDone);

    return () => {
      router.events.off("routeChangeStart", handleRouteStart);
      router.events.off("routeChangeComplete", handleRouteDone);
      router.events.off("routeChangeError", handleRouteDone);
    };
  }, [isAuthenticated, user]);

  useKeySearch();

  useEffect(() => {
    const handleAuthEvent = ({ payload }: { payload: { event: string } }) => {
      if (payload.event === "signIn") {
        setUserChanged(true); // Set userChanged to true when user changes
      }
      if (payload.event === "signOut") {
        clearSentryUser();
        queryClient.clear();
      }
    };

    // Subscribe to Hub events to detect authentication changes
    Hub.listen("auth", handleAuthEvent);

    return () => {
      // Unsubscribe from Hub events when component unmounts
      Hub.listen("auth", handleAuthEvent);
    };
  }, []);

  const isRenderOnboarding = useMemo(() => {
    return pathname == "/dashboard" && !user?.bio?.user?.isOnboarded;
  }, [pathname, user?.bio?.user?.isOnboarded]);

  return (
    <>
      <Head>
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no"
        />
      </Head>
      <ConfigProvider locale={locale} theme={theme}>
        <StyleProvider hashPriority="high">
          <QueryClientProvider client={queryClient}>
            {/* <Hydrate state={pageProps.dehydratedState}> */}
            <Authenticator.Provider>
              <AuthListener />
              <SessionProvider session={session} refetchInterval={5 * 60}>
                {isMounted && isRenderOnboarding && <OnBoarding />}
                <ErrorBoundary>
                  <Component {...pageProps} />
                  {pathname !== "/login" && <DevDebugTools />}
                </ErrorBoundary>
                <ModalResult />
                <PointsProvider />
                <ProtectedRoutes />
                <UserGuideVideo />
                {/* <UserGuide /> */}
                <ModalReadMore />
              </SessionProvider>
            </Authenticator.Provider>
            {/* </Hydrate> */}
          </QueryClientProvider>
        </StyleProvider>
      </ConfigProvider>
    </>
  );
}
